import { Moment } from 'moment';
import {
  ISpace,
  SpaceTypeCategoryMap,
} from 'spw-redux/modules/floorplan/types';
import {
  ISpaceTypeCategory,
  ISpaceTypeSubcategory,
} from 'spw-redux/modules/space_types/types';
import { INote } from 'spw-redux/modules/notes/types';
import { IPhoto } from 'spw-redux/modules/photo/types';
import {
  CUSTOM_WORKSPACE_COLORS,
  CUSTOM_MEETING_SPACE_COLORS,
  CUSTOM_AMENITY_SPACE_COLORS,
  CUSTOM_SUPPORT_SPACE_COLORS,
  EXISTING_SITE_ANALYSIS,
  PROGRAMMING,
} from 'spw-constants';
import { EntityList } from 'spw-redux/types';
import { SortingDirection } from 'spw-components/collab/types';
import { IUser } from 'spw-redux/modules/user/types';
import { ProgramSummaryCardType } from 'spw-esa-projects/program-summary/icon-selector-popover';
import { Team } from '../teams/types';
import { Client } from '../clients/types';
import { UserVM } from '../users/types';

export type ProjectStatus = 'draft' | 'billable' | 'template' | 'benchmark';

export enum UserRoleInProject {
  viewer = 'VIEWER',
  editor = 'EDITOR',
  projectOwner = 'PROJECT_OWNER',
  orgAdmin = 'ORG_ADMIN',
  teamAdmin = 'TEAM_ADMIN',
}

export const floorLevel = 'FLOOR';
export const buildingLevel = 'BUILDING';

export const HEADCOUNTS = {
  ARCHITECT: 'ARCHITECT',
  CLIENT: 'CLIENT',
};

export type HeadcountLevel = typeof buildingLevel | typeof floorLevel;

export type HeadcountType =
  | typeof HEADCOUNTS.ARCHITECT
  | typeof HEADCOUNTS.CLIENT;

export interface Address {
  city: string;
  state: string;
  countryCode?: string;
}

export interface ProgramSummaryCardIcon {
  cardType: ProgramSummaryCardType;
  iconPath: string;
}

export interface IProject extends Address {
  id?: number;
  client?: Partial<Client>;
  projectName: string;
  isMember?: boolean;
  buildings?: IBuilding[];
  teams?: Team[];
  requiresRequestToJoin?: boolean;
  status?: ProjectStatus;
  projectNumber?: string;
  type?: ProjectType;
  insertedAt?: Moment;
  numberOfDepartments?: number;
  numberOfScenarios?: number;
  createdBy?: string;
  year?: number;
  startYear?: number;
  clientMoveInYear?: number;
  isHeadquarters?: boolean;
  archived?: boolean;
  createdByUser?: IUser;
  projectOwner?: IUser;
  createdByUserId?: number;
  organizationId?: number;
  currentUserRole?: UserRoleInProject;
  spaceTypeCategories?: IProjectSpaceTypeCategory[];
  isMarkedAsFavoriteByUser?: boolean;
  isLinkedToBenchmarkReport?: boolean;
  updatedAt?: string;
  lastEditor?: IUser;
  headcountType?: HeadcountType;
  headcount?: Headcount;
  hasMissingBenchmarkData?: boolean;
  programSummaryCardIcons?: ProgramSummaryCardIcon[];
  allowBlankLocation?: boolean;
}

export type ProjectSlimForList = Pick<
  IProject,
  | 'projectName'
  | 'projectNumber'
  | 'type'
  | 'client'
  | 'projectOwner'
  | 'status'
  | 'insertedAt'
  | 'createdByUser'
  | 'city'
  | 'state'
  | 'requiresRequestToJoin'
  | 'lastEditor'
  | 'updatedAt'
  | 'teams'
  | 'isMarkedAsFavoriteByUser'
  | 'hasMissingBenchmarkData'
> & { id: number };

export type ProjectListItemVM = ProjectSlimForList & {
  action?: string;
};

export interface IProjectSpaceTypeCategory {
  id?: number;
  name: string;
  icon?: string;
  defaultSpaceTypeCategory: ISpaceTypeCategory;
  parentId?: number;
  subcategories?: IProjectSpaceTypeSubcategory[];
}

export interface IProjectSpaceTypeSubcategory
  extends Omit<
    IProjectSpaceTypeCategory,
    'icon' | 'subcategories' | 'defaultSpaceTypeCategory'
  > {
  parentId: number;
  defaultSpaceTypeCategory: ISpaceTypeSubcategory;
  includeInTotalWorkSeats?: boolean;
}

export type IProjectSpaceTypeCategoryOrSubcategory =
  | IProjectSpaceTypeCategory
  | IProjectSpaceTypeSubcategory;

export interface IProjectsState {
  cloningProjectId?: number;
  currentProjectType: string | null;
  currentProject: IProject;
  currentProjectColors?: {
    subcategories: Record<
      string,
      { color: string; gradientColorIndex: number }
    >;
  };
  isBuildingModalOpen: boolean;
  showNewBuildingModal: boolean;
  isClientModalOpen: boolean;
  isProjectModalOpen: boolean;
  openHeadcountModal: boolean;
  projects: IProject[];
  totalFilteredProjects: number;
  currentBuilding: IBuilding;
  spaceTypeCategories: ISpaceTypeCategory[];
  spaceTypeCategoriesMap: SpaceTypeCategoryMap | null;
  currentFloor: IFloor | null;
  currentProjectProfile: IProject;
  departments: IDepartment[];
  colors: IProjectColors;
  userFavoriteProjectsLegacy: IUserFavoriteProject[]; // deprecated
  organizationUsers: IUser[] | null;
  projectDetailSelectedTab: number;
  projectDetailNextTab: number;
  loadingProjects: boolean;
  projectEditorsList: EntityList<UserVM>;
  previouslyVisitedBenchmarkProjectId?: number;
}

export interface Headcount {
  client?: number;
  architect?: number;
  buildingId?: number | string;
  floorId?: number | string;
}

export interface IBuilding {
  id?: number;
  index?: number | null;
  circulationPercentage?: number;
  circulation?: number;
  programSubtotalUsf?: number;
  city: string;
  state?: string;
  countryCode?: string;
  streetAddress: string;
  floors?: IFloor[];
  numberOfFloors: number;
  squareFeet: number;
  address?: string;
  projectId?: number;
  programmingUsf?: number;
  photosListByDate?: IPhoto[];
  notesListByDate?: INote[];
  esaRsfLoadFactor?: number;
  headcount?: Headcount;
}

export interface IFloor {
  id: number;
  floorNumber: number;
  name: string;
  spaces?: ISpace[];
  departments: IDepartment[];
  headcount?: Headcount;
  totalFloorUsf: number;
  isMeasuredFromFloorplan?: boolean;
  floorplan?: { id: number } | null;
  rsfLoadFactor?: number;
  totalFloorRsf?: number;
  isRsfTotalLocked?: boolean;
}

export interface IDepartment {
  id?: number;
  projectId?: IProject['id'];
  name: string;
  secondaryIdentifier?: string;
  headcountArchitect: number;
  headcountClient: number;
  index: number;
}

export type ProjectType = typeof EXISTING_SITE_ANALYSIS | typeof PROGRAMMING;

export interface IProjectColors extends Record<string, unknown> {
  [CUSTOM_WORKSPACE_COLORS]: string[];
  [CUSTOM_MEETING_SPACE_COLORS]: string[];
  [CUSTOM_AMENITY_SPACE_COLORS]: string[];
  [CUSTOM_SUPPORT_SPACE_COLORS]: string[];
}

export interface IUserFavoriteProject {
  id?: number;
  index: number;
  projectId?: IProject['id'];
  userId?: IUser['id'];
}

export type ProjectsSortingParams = {
  isMarkedAsFavoriteByUser?: SortingDirection;
  client?: SortingDirection;
  projectName?: SortingDirection;
  status?: SortingDirection;
  projectOwner?: SortingDirection;
  type?: SortingDirection;
  insertedAt?: SortingDirection;
};

export type GetProjectsParams = {
  type?: ProjectType;
  status?: ProjectStatus;
  clientId?: number;
  page?: number;
  perPage?: number;
  teamId?: number;
  sort?: ProjectsSortingParams;
  search?: string;
};

export type ITransferredProject = Pick<IProject, 'id'> & {
  projectOwnerId: number;
};
